import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';
import { API_ENDPOINTS } from '../config/constants';
import '../styles/events.css';

const parseEventUtcDate = (dateValue) => {
  if (!dateValue) return new Date(NaN);
  if (dateValue instanceof Date) return dateValue;
  if (typeof dateValue === 'number') return new Date(dateValue);
  if (typeof dateValue !== 'string') return new Date(dateValue);

  const trimmed = dateValue.trim();
  const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(trimmed);
  const isoLike = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
  const normalized = hasTimezone ? isoLike : `${isoLike}Z`;

  return new Date(normalized);
};

const formatCountdown = (targetDate) => {
  const diff = parseEventUtcDate(targetDate) - new Date();
  if (diff <= 0) return null;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const secs = Math.floor((diff % (1000 * 60)) / 1000);
  return { days, hours, mins, secs };
};

const pad = (n) => String(n).padStart(2, '0');

const getPreferredPrice = (event) => {
  if (!Array.isArray(event?.prices) || event.prices.length === 0) {
    if (event?.price === undefined || event?.price === null || event?.price === '') {
      return null;
    }

    const parsedLegacyPrice = Number(event.price);
    if (!Number.isFinite(parsedLegacyPrice)) {
      return null;
    }

    return { currency: event.currency || 'INR', price: parsedLegacyPrice, isenabled: true };
  }

  const enabledPrices = event.prices.filter((item) => item.isenabled !== false);
  return enabledPrices.find((item) => item.currency === 'INR') || enabledPrices[0] || event.prices[0] || null;
};

const formatPriceLabel = (event) => {
  const preferred = getPreferredPrice(event);
  if (!preferred) {
    return 'RSVP';
  }

  if (Number(preferred.price) === 0) {
    return 'Free';
  }

  return `${preferred.currency} ${Number(preferred.price).toFixed(2)}`;
};

const EventDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const [rsvpForm, setRSVPForm] = useState({
    name: '', email: '', mobile: '', status: 'going', guests_count: 0,
  });
  const [rsvpLoading, setRSVPLoading] = useState(false);
  const [rsvpSuccess, setRSVPSuccess] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [stickyVisible, setStickyVisible] = useState(false);
  const heroRef = useRef(null);
  const cdRef = useRef(null);

  useEffect(() => {
    loadEvent();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        setStickyVisible(heroRef.current.getBoundingClientRect().bottom < 80);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!event || parseEventUtcDate(event.start_datetime) <= new Date()) return;
    const tick = () => setCountdown(formatCountdown(event.start_datetime));
    tick();
    cdRef.current = setInterval(tick, 1000);
    return () => clearInterval(cdRef.current);
  }, [event?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEvent = async () => {
    const eventId = searchParams.get('id');
    if (!eventId) { navigate('/events'); return; }
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/events/${eventId}`, {
        headers: { [TOKEN_HEADER]: generateToken() },
      });
      const data = await response.json();
      if (data.success && data.data) {
        setEvent(data.data);
        sessionStorage.setItem('selectedEvent', JSON.stringify(data.data));
      } else {
        setError('Event not found');
        setTimeout(() => navigate('/events'), 2000);
      }
    } catch (err) {
      console.error('Error fetching event:', err);
      setError('Failed to load event');
      setTimeout(() => navigate('/events'), 2000);
    } finally {
      setLoading(false);
    }
  };

  const formatDateLong = (d) =>
    parseEventUtcDate(d).toLocaleString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long',
      day: 'numeric', hour: 'numeric', minute: '2-digit',
    });

  const formatDateShort = (d) => {
    const dt = parseEventUtcDate(d);
    return `${dt.toLocaleDateString('en-US', { weekday: 'short' })}, ${dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · ${dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  };

  const handleRSVPSubmit = async (e) => {
    e.preventDefault();
    setRSVPLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/events/${event.id}/rsvp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [TOKEN_HEADER]: generateToken() },
        body: JSON.stringify(rsvpForm),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setRSVPSuccess(true);
        setRSVPForm({ name: '', email: '', mobile: '', status: 'going', guests_count: 0 });
        setEvent(prev => ({ ...prev, going_count: (prev.going_count || 0) + 1 }));
        setTimeout(() => { setShowRSVPModal(false); setRSVPSuccess(false); }, 2500);
      } else {
        alert(data.error || 'Failed to submit RSVP. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting RSVP:', err);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setRSVPLoading(false);
    }
  };

  const closeModal = () => { setShowRSVPModal(false); setRSVPSuccess(false); };

  // ── Loading / Error states ──
  if (loading) {
    return (
      <div className="ed-page">
        <div className="ep-loading" style={{ marginTop: 80 }}>
          <div className="ep-spinner" />
          <p>Loading event...</p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="ed-page">
        <div className="ep-empty" style={{ marginTop: 80 }}>
          <div className="ep-empty-icon">⚠️</div>
          <h3>{error || 'Event not found'}</h3>
          <p className="ep-muted">Redirecting to events page...</p>
        </div>
      </div>
    );
  }

  const isPast = parseEventUtcDate(event.start_datetime) <= new Date();
  const preferredPrice = getPreferredPrice(event);
  const enabledPrices = Array.isArray(event.prices)
    ? event.prices.filter((item) => item.isenabled !== false)
    : [];
  const isFree = event.is_free === true || !preferredPrice || Number(preferredPrice.price) === 0;
  const priceLabel = formatPriceLabel(event);
  const locationDisplay = event.location_type === 'virtual'
    ? '💻 Online'
    : event.location_name ? `📍 ${event.location_name}` : '📍 Location TBA';
  const seatsLeft = event.max_attendees
    ? Math.max(0, event.max_attendees - (event.going_count || 0))
    : null;
  const seatsPct = event.max_attendees
    ? Math.min(100, Math.round(((event.going_count || 0) / event.max_attendees) * 100))
    : null;

  return (
    <div className="ed-page">

      {/* ── HERO ── */}
      <header className="ed-hero" ref={heroRef}>
        <div className="ed-hero-glow" aria-hidden="true" />
        <div className="ed-hero-inner">

          <button className="ed-back-link" onClick={() => navigate('/events')}>
            ← All events
          </button>

          <div className="ed-hero-grid">
            {/* LEFT */}
            <div className="ed-hero-left">
              <div className="ed-badges">
                {event.category_name && (
                  <span className="ed-badge-category">{event.category_name}</span>
                )}
                {event.is_featured && (
                  <span className="ed-badge-featured">⭐ Featured</span>
                )}
                {isPast && (
                  <span className="ed-badge-past">Past Event</span>
                )}
                <span className="ed-badge-date">{formatDateShort(event.start_datetime)}</span>
              </div>

              <h1 className="ed-title">{event.title}</h1>

              {!isFree && preferredPrice && (
                <p className="ed-price-tagline">
                  Starting from <strong>{formatPriceLabel(event)}</strong>
                </p>
              )}
              {!isFree && enabledPrices.length > 1 && (
                <p className="ep-muted" style={{ marginBottom: '12px', fontSize: '14px' }}>
                  Also available in {enabledPrices
                    .map((item) => `${item.currency} ${Number(item.price).toFixed(2)}`)
                    .join(' · ')}
                </p>
              )}
              {isFree && (
                <p className="ed-free-tagline">Free Event — No registration fee</p>
              )}

              {event.description && (
                <p className="ed-hero-desc">
                  {event.description.length > 320
                    ? event.description.substring(0, 320) + '...'
                    : event.description}
                </p>
              )}

              <div className="ed-pills">
                {event.end_datetime && (
                  <span className="ed-pill">
                    🕐 {parseEventUtcDate(event.start_datetime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}–{parseEventUtcDate(event.end_datetime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </span>
                )}
                <span className="ed-pill">{locationDisplay}</span>
                {seatsLeft !== null && (
                  <span className="ed-pill">👥 {seatsLeft} seat{seatsLeft !== 1 ? 's' : ''} left</span>
                )}
                {event.timezone && (
                  <span className="ed-pill">🌐 {event.timezone}</span>
                )}
                {event.tags && event.tags.split(',').slice(0, 3).map(t => (
                  <span key={t.trim()} className="ed-pill">{t.trim()}</span>
                ))}
              </div>
            </div>

            {/* RIGHT — registration card */}
            {!isPast && (
              <div className="ed-reg-card">
                <div className="ed-reg-card-glow" aria-hidden="true" />
                <div className="ed-reg-card-inner">
                  <div className="ed-reg-from">
                    <span className="ed-reg-from-label">
                      {isFree ? '' : 'From'}
                    </span>
                    <span className="ed-reg-price">{priceLabel}</span>
                  </div>

                  {countdown && (
                    <div className="ep-countdown" style={{ marginBottom: 18 }}>
                      {[
                        { v: countdown.days, l: 'Days' },
                        { v: countdown.hours, l: 'Hrs' },
                        { v: countdown.mins, l: 'Min' },
                        { v: countdown.secs, l: 'Sec', red: true },
                      ].map(({ v, l, red }) => (
                        <div key={l} className="ep-cd-box">
                          <div className={`ep-cd-num${red ? ' ep-red' : ''}`}>{pad(v)}</div>
                          <div className="ep-cd-label">{l}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {seatsPct !== null && (
                    <div className="ed-seats-bar-wrap">
                      <div className="ed-seats-bar-labels">
                        <span className="ep-muted">{seatsLeft} of {event.max_attendees} seats left</span>
                        {seatsPct >= 70 && <span className="ed-filling-fast">Filling fast</span>}
                      </div>
                      <div className="ed-seats-bar-track">
                        <div className="ed-seats-bar-fill" style={{ width: `${seatsPct}%` }} />
                      </div>
                    </div>
                  )}

                  <button
                    className="ep-btn-primary"
                    style={{ width: '100%', justifyContent: 'center', marginBottom: 14, marginTop: 4 }}
                    onClick={() => setShowRSVPModal(true)}
                  >
                    Register now →
                  </button>

                  <div className="ed-reg-footer">
                    <span>👥 {event.going_count || 0} {isPast ? 'attended' : 'attending'}</span>
                    {event.registration_deadline && (
                      <span>Register by {parseEventUtcDate(event.registration_deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Past event — attendee count card */}
            {isPast && (
              <div className="ed-reg-card ed-reg-card-past">
                <div className="ed-reg-card-inner">
                  <div className="ed-past-attended">
                    <span className="ed-past-attended-n">{event.going_count || 0}</span>
                    <span className="ep-muted">people attended this event</span>
                  </div>
                  {event.video_url && (
                    <a
                      href={event.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ep-btn-primary"
                      style={{ width: '100%', justifyContent: 'center' }}
                    >
                      ▶ Watch Recording
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── ABOUT ── */}
      {event.description && (
        <section className="ed-section">
          <div className="ed-section-inner">
            <span className="ed-eyebrow">About this event</span>
            <h2 className="ed-section-title">What to expect</h2>
            <p className="ed-body-text">{event.description}</p>
          </div>
        </section>
      )}

      {/* ── EVENT DETAILS ── */}
      <section className="ed-section ed-section-dark">
        <div className="ed-section-inner">
          <span className="ed-eyebrow">Event details</span>
          <h2 className="ed-section-title">Date, time &amp; location</h2>
          <div className="ed-info-grid">
            <div className="ed-info-item">
              <span className="ed-info-icon">📅</span>
              <div>
                <div className="ed-info-label">Date &amp; Time</div>
                <div className="ed-info-value">{formatDateLong(event.start_datetime)}</div>
                {event.end_datetime && (
                  <div className="ed-info-sub">Ends: {formatDateLong(event.end_datetime)}</div>
                )}
              </div>
            </div>
            <div className="ed-info-item">
              <span className="ed-info-icon">{event.location_type === 'virtual' ? '💻' : '📍'}</span>
              <div>
                <div className="ed-info-label">Location</div>
                <div className="ed-info-value">
                  {event.location_url ? (
                    <a href={event.location_url} target="_blank" rel="noopener noreferrer" className="ed-link">
                      {event.location_type === 'virtual' ? 'Online Event' : event.location_name || 'Location TBA'}
                    </a>
                  ) : (
                    event.location_type === 'virtual' ? 'Online Event' : event.location_name || 'Location TBA'
                  )}
                </div>
                {(event.address || event.city) && (
                  <div className="ed-info-sub">
                    {[event.address, event.city, event.state, event.country].filter(Boolean).join(', ')}
                  </div>
                )}
              </div>
            </div>
            {event.going_count > 0 && (
              <div className="ed-info-item">
                <span className="ed-info-icon">👥</span>
                <div>
                  <div className="ed-info-label">Attendance</div>
                  <div className="ed-info-value">{event.going_count} {isPast ? 'attended' : 'attending'}</div>
                  {event.max_attendees && (
                    <div className="ed-info-sub">Limited to {event.max_attendees} attendees</div>
                  )}
                </div>
              </div>
            )}
            {event.timezone && (
              <div className="ed-info-item">
                <span className="ed-info-icon">🌐</span>
                <div>
                  <div className="ed-info-label">Timezone</div>
                  <div className="ed-info-value">{event.timezone}</div>
                </div>
              </div>
            )}
            {event.registration_deadline && !isPast && (
              <div className="ed-info-item">
                <span className="ed-info-icon">⏰</span>
                <div>
                  <div className="ed-info-label">Registration deadline</div>
                  <div className="ed-info-value">{formatDateLong(event.registration_deadline)}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── TAGS ── */}
      {event.tags && (
        <section className="ed-section">
          <div className="ed-section-inner">
            <span className="ed-eyebrow">Topics</span>
            <div className="ed-tags-wrap">
              {event.tags.split(',').map((tag, i) => (
                <span key={i} className="ed-tag">{tag.trim()}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── JOIN ONLINE ── */}
      {event.meeting_link && !isPast && (
        <section className="ed-section ed-section-dark">
          <div className="ed-section-inner">
            <span className="ed-eyebrow">Join the event</span>
            <h2 className="ed-section-title">Ready to join?</h2>
            <p className="ep-muted" style={{ marginBottom: 20 }}>
              Use the link below on the day of the event to join online.
            </p>
            <a
              href={event.meeting_link}
              target="_blank"
              rel="noopener noreferrer"
              className="ep-btn-primary"
            >
              💻 Click here to join the meeting →
            </a>
          </div>
        </section>
      )}

      {/* ── VIDEO ── */}
      {event.video_url && (
        <section className="ed-section">
          <div className="ed-section-inner">
            <span className="ed-eyebrow">Recording</span>
            <h2 className="ed-section-title">Event video</h2>
            <div className="ed-video-embed">
              <iframe
                src={event.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                title="Event Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      {/* ── ORGANIZER ── */}
      {event.organizer && (
        <section className="ed-section ed-section-dark">
          <div className="ed-section-inner">
            <span className="ed-eyebrow">Hosted by</span>
            <div className="ed-organizer-card">
              <div className="ed-organizer-avatar">
                {event.organizer.trim().charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="ed-organizer-name">{event.organizer}</div>
                <div className="ep-muted" style={{ fontSize: 14 }}>Event Organizer</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── FINAL CTA ── */}
      {!isPast && (
        <section className="ed-cta-section">
          <div className="ed-section-inner" style={{ textAlign: 'center' }}>
            <h2 className="ed-cta-title">Ready to join?</h2>
            <p className="ep-muted ed-cta-sub">
              {seatsLeft !== null
                ? `Only ${seatsLeft} seat${seatsLeft !== 1 ? 's' : ''} remaining — don't miss out.`
                : 'Secure your spot before it fills up.'}
            </p>
            <button className="ep-btn-primary ep-btn-lg" onClick={() => setShowRSVPModal(true)}>
              {isFree ? 'Join Free →' : `Register — ${priceLabel} →`}
            </button>
          </div>
        </section>
      )}

      {/* ── STICKY BAR ── */}
      {!isPast && (
        <div className={`ed-sticky-bar${stickyVisible ? ' ed-sticky-bar-visible' : ''}`}>
          <div className="ed-sticky-inner">
            <div className="ed-sticky-info">
              <div className="ed-sticky-title">{event.title}</div>
              <div className="ed-sticky-meta">
                {formatDateShort(event.start_datetime)}
                {seatsLeft !== null && ` · ${seatsLeft} seats left`}
              </div>
            </div>
            <div className="ed-sticky-right">
              <span className="ed-sticky-price">{priceLabel}</span>
              <button className="ep-btn-primary ep-btn-sm" onClick={() => setShowRSVPModal(true)}>
                Register →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── RSVP MODAL ── */}
      {showRSVPModal && (
        <div className="ep-modal-overlay" onClick={closeModal}>
          <div className="ep-modal" onClick={e => e.stopPropagation()}>
            <button className="ep-modal-close" onClick={closeModal}>✕</button>

            {rsvpSuccess ? (
              <div className="ep-rsvp-success">
                <span className="ep-success-icon">✅</span>
                <h2>You're in!</h2>
                <p>
                  Your seat for <strong>{event.title}</strong> is reserved.
                </p>
                <p className="ep-muted">We've emailed your joining details.</p>
              </div>
            ) : (
              <>
                <span className="ed-modal-eyebrow">Reserve your seat</span>
                <h2 className="ep-modal-title">{event.title}</h2>
                <p className="ep-modal-sub">{formatDateShort(event.start_datetime)}</p>

                <form onSubmit={handleRSVPSubmit} className="ep-rsvp-form">
                  <div className="ep-form-group">
                    <label htmlFor="ed-name">Full Name *</label>
                    <input
                      type="text"
                      id="ed-name"
                      value={rsvpForm.name}
                      onChange={e => setRSVPForm({ ...rsvpForm, name: e.target.value })}
                      required
                      placeholder="Your full name"
                    />
                  </div>
                  <div className="ep-form-group">
                    <label htmlFor="ed-email">Email Address *</label>
                    <input
                      type="email"
                      id="ed-email"
                      value={rsvpForm.email}
                      onChange={e => setRSVPForm({ ...rsvpForm, email: e.target.value })}
                      required
                      placeholder="you@email.com"
                    />
                  </div>
                  <div className="ep-form-group">
                    <label htmlFor="ed-mobile">Mobile (Optional)</label>
                    <input
                      type="tel"
                      id="ed-mobile"
                      value={rsvpForm.mobile}
                      onChange={e => setRSVPForm({ ...rsvpForm, mobile: e.target.value })}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <div className="ep-form-group">
                    <label>RSVP Status *</label>
                    <div className="ep-status-options">
                      {[{ val: 'going', icon: '✓', label: 'Going' }, { val: 'interested', icon: '⭐', label: 'Interested' }].map(({ val, icon, label }) => (
                        <label key={val} className="ep-status-option">
                          <input
                            type="radio"
                            name="ed-status"
                            value={val}
                            checked={rsvpForm.status === val}
                            onChange={e => setRSVPForm({ ...rsvpForm, status: e.target.value })}
                          />
                          <span className="ep-status-label"><span>{icon}</span> {label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="ep-form-group">
                    <label htmlFor="ed-guests">Number of Guests</label>
                    <select
                      id="ed-guests"
                      value={rsvpForm.guests_count}
                      onChange={e => setRSVPForm({ ...rsvpForm, guests_count: parseInt(e.target.value) })}
                    >
                      <option value="0">Just me</option>
                      <option value="1">+1 guest</option>
                      <option value="2">+2 guests</option>
                      <option value="3">+3 guests</option>
                      <option value="4">+4 guests</option>
                      <option value="5">+5 guests</option>
                    </select>
                  </div>
                  <button type="submit" className="ep-submit-btn" disabled={rsvpLoading}>
                    {rsvpLoading ? 'Submitting...' : `Confirm — ${priceLabel}`}
                  </button>
                  <p className="ed-modal-note">Reserve now · we'll email your joining link.</p>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetails;
